import type { Partner } from "@expert/shared-types";

/**
 * Maps a raw partner/client signal — an Amazon Connect contact attribute or a CSD
 * `PARTNER`/`CLIENT` code — to a canonical partner key.
 *
 * Returns `undefined` for absent OR blank input (`""`, whitespace) so a caller's
 * `?? fallback` chain engages. Previously a blank value was returned verbatim, which
 * put `partner:""` on the eligibility request and produced a Zod 400 — see the
 * Verizon voice empty-`PARTNER` incident (2026-08-05), where Connect began emitting
 * an empty-string `PARTNER` attribute while the CSD `CLIENT` code (`HZNVZN`) was still
 * populated.
 */
export default function normalizePartner(value: string | undefined): Partner | undefined {
    const normalized = value?.trim().toLowerCase();
    switch (normalized) {
        case "at&t":
            return "att";
        case "vzw":
        case "vzn":
        case "hznvzn": // CSD CLIENT code for Verizon
            return "verizon";
        // The McAfee IVR still emits the retired `mcafee` key. Both McAfee cohorts (Retention
        // and Tech Support) are served by the `mcafeetechsupport` partner on this instance, and
        // `mcafee` is no longer a member of `Partner` — without this alias the session resolves
        // the legacy `mcafee` salesPartnerConfig and the expert sees the wrong offer catalog.
        case "mcafee":
            return "mcafeetechsupport";
        default:
            return normalized || undefined;
    }
}
